import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { privateRequest } from "../utils/api";

export interface Role {
  id: number;
  name: string;
  guard_name: string;
  created_at?: string;
  updated_at?: string;
}

export interface Permission {
  id: number;
  name: string;
  guard_name: string;
  created_at?: string;
  updated_at?: string;
}

export interface User {
  id: number;
  name: string;
  email: string;
  email_verified_at?: string;
  locale?: string;
  avatar?: string;
  bio?: string;
  last_login_at?: string;
  created_at: string;
  updated_at: string;
  roles?: Role[];
  permissions?: Permission[];
}

export interface UsersResponse {
  data: User[];
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
}

export interface UsersQueryParams {
  per_page?: number;
  page?: number;
  search?: string;
  role?: string;
}

export interface CreateUserData {
  name: string;
  email: string;
  password: string;
  locale?: string;
  roles?: string[];
}

export interface UpdateUserData {
  name?: string;
  email?: string;
  password?: string;
  locale?: string;
  roles?: string[];
}

// Get all users
export const useUsers = (params: UsersQueryParams = {}) => {
  return useQuery({
    queryKey: ["users", params],
    queryFn: async () => {
      const response = await privateRequest.get<{
        success: boolean;
        data: UsersResponse;
        message: string;
      }>("/users", { params });
      return response.data.data;
    },
  });
};

// Get single user
export const useUser = (id: number) => {
  return useQuery({
    queryKey: ["user", id],
    queryFn: async () => {
      const response = await privateRequest.get<{
        success: boolean;
        data: { user: User };
        message: string;
      }>(`/users/${id}`);
      return response.data.data.user;
    },
    enabled: !!id,
  });
};

// Get all roles
export const useRoles = () => {
  return useQuery({
    queryKey: ["roles"],
    queryFn: async () => {
      const response = await privateRequest.get<{
        success: boolean;
        data: { roles: Role[] };
        message: string;
      }>("/users/roles");
      return response.data.data.roles;
    },
  });
};

// Create user mutation
export const useCreateUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateUserData) => {
      const response = await privateRequest.post<{
        success: boolean;
        data: { user: User };
        message: string;
      }>("/users", data);
      return response.data.data.user;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
    },
  });
};

// Update user mutation
export const useUpdateUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: UpdateUserData }) => {
      const response = await privateRequest.put<{
        success: boolean;
        data: { user: User };
        message: string;
      }>(`/users/${id}`, data);
      return response.data.data.user;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
      queryClient.invalidateQueries({ queryKey: ["user", variables.id] });
    },
  });
};

// Delete user mutation
export const useDeleteUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number) => {
      const response = await privateRequest.delete<{
        success: boolean;
        data: null;
        message: string;
      }>(`/users/${id}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
    },
  });
};
